import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { hieraApi, type PageRow, type PageTag } from "../lib/api";
import { charCount, docToMarkdown, extractLinks, headings, wordCount } from "../lib/docText";
import { t, type Lang } from "../lib/i18n";
import { PageEditor } from "../editor/PageEditor";
import { readEditMode, readToolbar, readWide, writeWide } from "../lib/prefs";
import { canEditRole } from "../lib/roles";
import { useNotePageTab } from "../shell/pageTabContext";

type Props = { lang: Lang };
type Panel = "comments" | "toc" | "history" | "details" | null;

const SHARE_PRESETS = [
  { minutes: 1, key: "1" },
  { minutes: 5, key: "5" },
  { minutes: 30, key: "30" },
  { minutes: 60, key: "hour" },
  { minutes: 180, key: "hours3" },
  { minutes: 360, key: "hours6" },
  { minutes: 720, key: "hours12" },
  { minutes: 1440, key: "day" },
] as const;

function watchSet(): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem("hiera.watch") || "[]") as string[];
    return new Set(raw);
  } catch {
    return new Set();
  }
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function relativeTime(iso: string | undefined, lang: Lang): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return new Date(iso).toLocaleString(lang === "ru" ? "ru-RU" : "en-GB");
  const min = Math.floor(ms / 60000);
  if (min < 1) return lang === "ru" ? "только что" : "just now";
  if (min < 60) return lang === "ru" ? `${min} мин. назад` : `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return lang === "ru" ? `${h} ч. назад` : `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return lang === "ru" ? `${d} дн. назад` : `${d}d ago`;
  return new Date(iso).toLocaleDateString(lang === "ru" ? "ru-RU" : "en-GB");
}

export function DocumentPage({ lang }: Props) {
  const { pageId = "" } = useParams();
  const nav = useNavigate();
  const [page, setPage] = useState<PageRow | null>(null);
  const [role, setRole] = useState("observer");
  const [title, setTitle] = useState("");
  const [editable, setEditable] = useState(() => readEditMode() === "edit");
  const [flash, setFlash] = useState("");
  const [spaceTitle, setSpaceTitle] = useState("");
  const [menu, setMenu] = useState<"share" | "more" | "move" | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [wide, setWide] = useState(readWide);
  const toolbar = readToolbar();
  const [starred, setStarred] = useState(false);
  const [watching, setWatching] = useState(false);
  const [shareMinutes, setShareMinutes] = useState(15);
  const [shareUrl, setShareUrl] = useState("");
  const [history, setHistory] = useState<Array<{ id: string; title: string; created_at: string; editor?: string }>>([]);
  const [siblings, setSiblings] = useState<Array<{ id: string; title: string }>>([]);
  const [tags, setTags] = useState<PageTag[]>([]);
  const [spaceTags, setSpaceTags] = useState<PageTag[]>([]);
  const [tagQuery, setTagQuery] = useState("");
  const [tagOpen, setTagOpen] = useState(false);
  const noteTab = useNotePageTab();

  async function load() {
    const res = await hieraApi.page(pageId);
    setPage(res.page);
    setRole(res.role);
    setTitle(res.draft?.title || res.page.title);
    setEditable(canEditRole(res.role) && readEditMode() === "edit");
    setWatching(watchSet().has(res.page.id));
    setTags(res.tags || []);
    const sp = await hieraApi.space(res.page.space_id);
    setSpaceTitle(String(sp.space.title || ""));
    const home = await hieraApi.home();
    setStarred(home.favorites.some((row) => String(row.page_id || "") === res.page.id));
  }

  useEffect(() => {
    setMenu(null);
    setPanel(null);
    setShareUrl("");
    setTagQuery("");
    setTagOpen(false);
    setHistory([]);
    setSpaceTags([]);
    void load().catch((err: unknown) => {
      if ((err as { status?: number }).status !== 404) nav("/");
    });
  }, [pageId]);

  useEffect(() => {
    if (!page) return;
    noteTab({ id: page.id, title: title || page.title || "Untitled", spaceId: page.space_id });
  }, [page, title, noteTab]);

  const links = useMemo(() => (page ? extractLinks(page.body) : []), [page]);
  const words = page ? wordCount(page.body) : 0;
  const chars = page ? charCount(page.body) : 0;
  const authorName = (page?.created_by_name || page?.updated_by_name || "").trim();
  const editorName = (page?.updated_by_name || authorName).trim();

  const tagSuggestions = useMemo(() => {
    const q = tagQuery.trim().toLowerCase();
    const taken = new Set(tags.map((row) => row.id));
    return spaceTags
      .filter((row) => !taken.has(row.id))
      .filter((row) => !q || row.name.toLowerCase().includes(q))
      .slice(0, 12);
  }, [spaceTags, tags, tagQuery]);

  function note(text: string) {
    setFlash(text);
    window.setTimeout(() => setFlash(""), 1600);
  }

  async function copy(text: string) {
    await navigator.clipboard.writeText(text);
    note(t(lang, "copied"));
    setMenu(null);
  }

  async function openDetails() {
    if (!page) return;
    const next = panel === "details" ? null : "details";
    setPanel(next);
    if (next !== "details") return;
    const [rev, st] = await Promise.all([
      hieraApi.revisions(page.id),
      hieraApi.spaceTags(page.space_id).catch(() => ({ tags: [] as PageTag[] })),
    ]);
    setHistory(rev.revisions);
    setSpaceTags(st.tags);
  }

  async function addTag(name: string) {
    if (!page || !canEditRole(role)) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const res = await hieraApi.addPageTag(page.id, trimmed);
      setTags((prev) => (prev.some((row) => row.id === res.tag.id) ? prev : [...prev, res.tag].sort((a, b) => a.name.localeCompare(b.name))));
      setSpaceTags((prev) => (prev.some((row) => row.id === res.tag.id) ? prev : [...prev, res.tag].sort((a, b) => a.name.localeCompare(b.name))));
      setTagQuery("");
      setTagOpen(false);
    } catch {
      /* schema_pending */
    }
  }

  async function removeTag(tagId: string) {
    if (!page || !canEditRole(role)) return;
    await hieraApi.removePageTag(page.id, tagId);
    setTags((prev) => prev.filter((row) => row.id !== tagId));
  }

  if (!page) return <div className="pad">…</div>;

  const canEdit = canEditRole(role);
  const toc = headings(page.body);
  const crumbs = (
    <nav className="crumbs">
      <Link to="/">Hiera</Link>
      <span>/</span>
      <Link to={`/s/${page.space_id}`}>{spaceTitle || "…"}</Link>
    </nav>
  );

  const locale = lang === "ru" ? "ru-RU" : "en-GB";

  const metaRow = (
    <div className="doc-meta">
      <span className="doc-meta-author">
        <span className="doc-meta-avatar" aria-hidden>
          {initials(authorName || "?")}
        </span>
        <span>
          {t(lang, "byAuthor")} {authorName || "—"}
        </span>
      </span>
      <button
        type="button"
        className={`doc-meta-btn${panel === "details" ? " is-on" : ""}`}
        aria-label={t(lang, "details")}
        title={t(lang, "details")}
        onClick={() => void openDetails()}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 10v6M12 7.5h.01" />
        </svg>
      </button>
      <button
        type="button"
        className={`doc-meta-btn${panel === "history" ? " is-on" : ""}`}
        aria-label={t(lang, "history")}
        title={t(lang, "history")}
        onClick={() => {
          setPanel(panel === "history" ? null : "history");
          if (panel !== "history") void hieraApi.revisions(page.id).then((res) => setHistory(res.revisions));
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M3 12a9 9 0 1 0 3-6.7" />
          <path d="M3 4v5h5M12 7v5l3 2" />
        </svg>
      </button>
      {tags.length ? (
        <div className="doc-meta-tags">
          {tags.map((tag) => (
            <span key={tag.id} className="doc-tag">
              {tag.name}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className={`doc-shell${wide ? " is-wide" : ""}`}>
      <div className="doc-main">
        <div className="doc-bar">
          {crumbs}
          <div className="doc-actions">
            {canEdit ? (
              <div className="mode-switch" role="group">
                <button type="button" className={editable ? "is-on" : ""} onClick={() => setEditable(true)}>
                  {t(lang, "edit")}
                </button>
                <button type="button" className={!editable ? "is-on" : ""} onClick={() => setEditable(false)}>
                  {t(lang, "read")}
                </button>
              </div>
            ) : (
              <span className="pill pill--ok">{t(lang, "read")}</span>
            )}
            <div className="pop-wrap">
              <button type="button" className="tool-btn" onClick={() => setMenu(menu === "share" ? null : "share")}>
                {t(lang, "share")}
              </button>
              {menu === "share" ? (
                <div className="pop">
                  <p className="pop-title">{t(lang, "shareHint")}</p>
                  <div className="share-presets">
                    {SHARE_PRESETS.map((item) => (
                      <button
                        key={item.minutes}
                        type="button"
                        className={shareMinutes === item.minutes ? "is-on" : ""}
                        onClick={() => setShareMinutes(item.minutes)}
                      >
                        {item.key === "1" || item.key === "5" || item.key === "30" ? item.key : t(lang, item.key)}
                      </button>
                    ))}
                  </div>
                  <label className="share-custom">
                    {t(lang, "shareCustom")}
                    <input
                      type="number"
                      min={1}
                      max={43200}
                      value={shareMinutes}
                      onChange={(e) => setShareMinutes(Number(e.target.value) || 1)}
                    />
                  </label>
                  <button
                    type="button"
                    className="btn"
                    onClick={async () => {
                      try {
                        const res = await hieraApi.createShare(page.id, shareMinutes);
                        const url = `${window.location.origin}/share/${res.token}`;
                        setShareUrl(url);
                        await navigator.clipboard.writeText(url);
                        note(t(lang, "copied"));
                      } catch {
                        note(t(lang, "shareGone"));
                      }
                    }}
                  >
                    {t(lang, "shareCreate")}
                  </button>
                  {shareUrl ? (
                    <button
                      type="button"
                      className="share-url"
                      title={t(lang, "shareCopy")}
                      aria-label={t(lang, "shareCopy")}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(shareUrl);
                          note(t(lang, "copied"));
                        } catch {
                          note(t(lang, "shareGone"));
                        }
                      }}
                    >
                      {shareUrl}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className={`icon-tool${panel === "comments" ? " is-on" : ""}`}
              aria-label={t(lang, "comments")}
              onClick={() => setPanel(panel === "comments" ? null : "comments")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M5 6h14v9H8l-3 3V6z" />
              </svg>
            </button>
            <button
              type="button"
              className={`icon-tool${panel === "toc" ? " is-on" : ""}`}
              aria-label={t(lang, "toc")}
              onClick={() => setPanel(panel === "toc" ? null : "toc")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />
              </svg>
            </button>
            <div className="pop-wrap">
              <button
                type="button"
                className="icon-tool"
                aria-label="…"
                onClick={() => setMenu(menu === "more" || menu === "move" ? null : "more")}
              >
                ···
              </button>
              {menu === "more" || menu === "move" ? (
                <div className="pop menu-pop">
                  {menu === "move" ? (
                    <>
                      <button type="button" onClick={() => void hieraApi.patchPage(page.id, { parentId: null, updatedAt: page.updated_at }).then(() => setMenu(null))}>
                        {t(lang, "moveRoot")}
                      </button>
                      {siblings.filter((row) => row.id !== page.id).map((row) => (
                        <button
                          key={row.id}
                          type="button"
                          onClick={() => void hieraApi.patchPage(page.id, { parentId: row.id, updatedAt: page.updated_at }).then(() => setMenu(null))}
                        >
                          {row.title || "Untitled"}
                        </button>
                      ))}
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={() => void copy(window.location.href)}>{t(lang, "copyLink")}</button>
                      <button type="button" onClick={() => void copy(`# ${title}\n\n${docToMarkdown(page.body)}`)}>{t(lang, "copyMd")}</button>
                      <button
                        type="button"
                        onClick={() => {
                          const next = !starred;
                          setStarred(next);
                          void (next ? hieraApi.favorite(page.id) : hieraApi.unfavorite(page.id));
                          setMenu(null);
                        }}
                      >
                        {starred ? t(lang, "removeFavorite") : t(lang, "addFavorite")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const next = new Set(watchSet());
                          if (next.has(page.id)) next.delete(page.id);
                          else next.add(page.id);
                          localStorage.setItem("hiera.watch", JSON.stringify([...next]));
                          setWatching(next.has(page.id));
                          setMenu(null);
                        }}
                      >
                        {watching ? t(lang, "unwatch") : t(lang, "watch")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const next = !wide;
                          setWide(next);
                          writeWide(next);
                          setMenu(null);
                        }}
                      >
                        {t(lang, "fullWidth")} {wide ? "●" : "○"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMenu(null);
                          void openDetails();
                        }}
                      >
                        {t(lang, "details")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMenu(null);
                          setPanel("history");
                          void hieraApi.revisions(page.id).then((res) => setHistory(res.revisions));
                        }}
                      >
                        {t(lang, "history")}
                      </button>
                      {canEdit ? (
                        <button
                          type="button"
                          onClick={() => {
                            const status = page.status === "needs_review" ? "published" : "needs_review";
                            void hieraApi.patchPage(page.id, { status, updatedAt: page.updated_at }).then((res) => {
                              setPage(res.page);
                              setMenu(null);
                            });
                          }}
                        >
                          {page.status === "needs_review" ? t(lang, "clearReview") : t(lang, "addReview")}
                        </button>
                      ) : null}
                      {canEdit ? (
                        <button
                          type="button"
                          onClick={() => {
                            void hieraApi.pages(page.space_id).then((res) => {
                              setSiblings(res.pages.map((row) => ({ id: row.id, title: row.title })));
                              setMenu("move");
                            });
                          }}
                        >
                          {t(lang, "move")}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => {
                          const blob = new Blob([`# ${title}\n\n${docToMarkdown(page.body)}\n`], { type: "text/markdown" });
                          const a = document.createElement("a");
                          a.href = URL.createObjectURL(blob);
                          a.download = `${title || "page"}.md`;
                          a.click();
                          URL.revokeObjectURL(a.href);
                          setMenu(null);
                        }}
                      >
                        {t(lang, "exportMd")}
                      </button>
                      <button type="button" onClick={() => { setMenu(null); window.print(); }}>{t(lang, "printPdf")}</button>
                      {canEdit ? (
                        <button
                          type="button"
                          className="danger"
                          onClick={() => {
                            if (!window.confirm(t(lang, "trash"))) return;
                            void hieraApi.deletePage(page.id).then(() => nav(`/s/${page.space_id}`));
                          }}
                        >
                          {t(lang, "trash")}
                        </button>
                      ) : null}
                      <p className="menu-meta">
                        {t(lang, "words")}: {words}
                        {page.created_at ? ` · ${t(lang, "created")}: ${new Date(page.created_at).toLocaleString(locale)}` : ""}
                      </p>
                    </>
                  )}
                </div>
              ) : null}
            </div>
            {flash ? <span className="save-dot" role="status" aria-label={flash} /> : null}
          </div>
        </div>
        <div className="doc pad">
          <PageEditor
            key={page.id}
            pageId={page.id}
            spaceId={page.space_id}
            initialBody={page.body}
            initialTitle={title}
            updatedAt={page.updated_at}
            editable={editable && canEdit}
            toolbar={toolbar && editable && canEdit}
            belowTitle={metaRow}
            onTitleChange={setTitle}
            onSaved={({ updatedAt, body, flash: f }) => {
              setPage((p) => (p ? { ...p, updated_at: updatedAt, ...(body !== undefined ? { body } : {}) } : p));
              if (f) note(f);
            }}
            langSavedLabel={t(lang, "saved")}
            langAutosavedLabel={t(lang, "autosaved")}
          />
        </div>
      </div>
      {panel ? (
        <aside className="doc-panel">
          <div className="doc-panel-head">
            <strong>
              {panel === "comments"
                ? t(lang, "comments")
                : panel === "toc"
                  ? t(lang, "toc")
                  : panel === "details"
                    ? t(lang, "details")
                    : t(lang, "history")}
            </strong>
            <button type="button" onClick={() => setPanel(null)} aria-label="×">×</button>
          </div>
          {panel === "comments" ? <p className="muted">{t(lang, "commentsEmpty")}</p> : null}
          {panel === "toc" ? (
            toc.length ? (
              <ul className="toc-list">
                {toc.map((item, i) => (
                  <li key={`${item.text}-${i}`} style={{ paddingLeft: (item.level - 1) * 12 }}>
                    <button
                      type="button"
                      onClick={() => {
                        const nodes = document.querySelectorAll(".tiptap h1, .tiptap h2, .tiptap h3");
                        nodes[i]?.scrollIntoView({ block: "start" });
                      }}
                    >
                      {item.text}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">{t(lang, "tocEmpty")}</p>
            )
          ) : null}
          {panel === "history" ? (
            history.length ? (
              <ul className="toc-list">
                {history.map((row) => (
                  <li key={row.id}>
                    <span>{row.editor || row.title || "Untitled"}</span>
                    <time>{new Date(row.created_at).toLocaleString(locale)}</time>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">{t(lang, "historyEmpty")}</p>
            )
          ) : null}
          {panel === "details" ? (
            <div className="details-panel">
              <section className="details-block">
                <div className="details-row">
                  <span className="muted">{t(lang, "createdBy")}</span>
                  <span className="details-who">
                    <span className="doc-meta-avatar sm" aria-hidden>{initials(authorName || "?")}</span>
                    {authorName || "—"}
                  </span>
                </div>
                <div className="details-row">
                  <span className="muted">{t(lang, "lastEditedBy")}</span>
                  <span className="details-who">
                    <span className="doc-meta-avatar sm" aria-hidden>{initials(editorName || "?")}</span>
                    {editorName || "—"}
                  </span>
                </div>
              </section>

              <section className="details-block">
                <h4>{t(lang, "stats")}</h4>
                <div className="details-row">
                  <span className="muted">{t(lang, "words")}</span>
                  <span>{words}</span>
                </div>
                <div className="details-row">
                  <span className="muted">{t(lang, "chars")}</span>
                  <span>{chars}</span>
                </div>
                <div className="details-row">
                  <span className="muted">{t(lang, "created")}</span>
                  <span>{page.created_at ? new Date(page.created_at).toLocaleString(locale) : "—"}</span>
                </div>
                <div className="details-row">
                  <span className="muted">{t(lang, "lastUpdate")}</span>
                  <span>{relativeTime(page.updated_at, lang)}</span>
                </div>
              </section>

              <section className="details-block">
                <h4>{t(lang, "history")}</h4>
                {history.length ? (
                  <ul className="toc-list">
                    {history.slice(0, 8).map((row) => (
                      <li key={row.id}>
                        <span>{row.editor || "—"}</span>
                        <time>{new Date(row.created_at).toLocaleString(locale)}</time>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">{t(lang, "historyEmpty")}</p>
                )}
              </section>

              <section className="details-block">
                <h4>{t(lang, "backlinks")}</h4>
                <div className="details-row">
                  <span className="muted">{t(lang, "linksIn")}</span>
                  <span>0</span>
                </div>
                <div className="details-row">
                  <span className="muted">{t(lang, "linksOut")}</span>
                  <span>{links.length}</span>
                </div>
                {links.length ? (
                  <ul className="details-links">
                    {links.map((link) => (
                      <li key={link.href}>
                        <a href={link.href} target="_blank" rel="noreferrer">
                          {link.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">{t(lang, "linksEmpty")}</p>
                )}
              </section>

              <section className="details-block">
                <h4>{t(lang, "tags")}</h4>
                <div className="doc-meta-tags details-tags">
                  {tags.map((tag) => (
                    <span key={tag.id} className="doc-tag">
                      {tag.name}
                      {canEdit ? (
                        <button type="button" aria-label="×" onClick={() => void removeTag(tag.id)}>
                          ×
                        </button>
                      ) : null}
                    </span>
                  ))}
                </div>
                {canEdit ? (
                  <div className="tag-picker">
                    {!tagOpen ? (
                      <button type="button" className="tag-add" onClick={() => setTagOpen(true)}>
                        + {t(lang, "addTag")}
                      </button>
                    ) : (
                      <>
                        <input
                          autoFocus
                          className="tag-input"
                          value={tagQuery}
                          placeholder={t(lang, "tagFind")}
                          onChange={(e) => setTagQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              setTagOpen(false);
                              setTagQuery("");
                            }
                            if (e.key === "Enter") {
                              e.preventDefault();
                              void addTag(tagQuery);
                            }
                          }}
                          onBlur={() => {
                            window.setTimeout(() => {
                              if (!tagQuery.trim()) setTagOpen(false);
                            }, 120);
                          }}
                        />
                        <ul className="tag-suggest">
                          {tagSuggestions.map((tag) => (
                            <li key={tag.id}>
                              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => void addTag(tag.name)}>
                                {tag.name}
                              </button>
                            </li>
                          ))}
                          {!tagSuggestions.length ? (
                            <li className="muted">{tagQuery.trim() ? t(lang, "tagsNoneMatch") : t(lang, "tagsEmpty")}</li>
                          ) : null}
                        </ul>
                      </>
                    )}
                  </div>
                ) : null}
              </section>
            </div>
          ) : null}
        </aside>
      ) : null}
    </div>
  );
}
